import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectById, getProjectTaskById } from '../../../redux/MainAction';
import { Modal } from 'react-responsive-modal';
import {
  MdAttachMoney,
  MdPeople,
  MdInfo,
  MdPerson,
  MdClose,
} from 'react-icons/md';
import './ProjectDetails.css';

const ProjectTaskDetails = ({ open, onClose, ID }) => {
  const dispatch = useDispatch();
  const projectTaskData = useSelector(
    (state) => state?.Webtracker?.projectTaskById
  );

  useEffect(() => {
    if (ID) getProjectTaskDetails();
  }, [ID]);

  const getProjectTaskDetails = () => {
    dispatch(
      getProjectTaskById({
        id: ID,
      })
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const statusClass =
      status === 'ACTIVE' ? 'badge-success' : 'badge-secondary';
    return <span className={`badge ${statusClass}`}>{status}</span>;
  };

  const getTaskStatusBadge = (status) => {
    const statusClass = status === 'OPEN' ? 'badge-success' : 'badge-danger';
    return <span className={`badge ${statusClass}`}>{status}</span>;
  };

  const getPriorityStatusBadge = (status) => {
    const statusClass =
      status === 'URGENT'
        ? 'badge-danger'
        : status === 'HIGH'
          ? 'badge-primary'
          : 'badge-secondary';
    return <span className={`badge ${statusClass}`}>{status}</span>;
  };

  // Members
  const [expandedMemberId, setExpandedMemberId] = useState(null);

  const toggleMember = (id) => {
    setExpandedMemberId((prev) => (prev === id ? null : id));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      showCloseIcon={false}
      classNames={{ modal: 'custom-modal project-details-modal' }}
      center
    >
      <div className="project-details-container">
        <div className="details-header">
          <div className="header-content">
            <h5 className="project-title">
              {projectTaskData?.name || 'Project Details'}
            </h5>
            <div className="header-meta">
              <span className="badge badge-secondary">
                ID: {projectTaskData?.taskId}
              </span>

              {getStatusBadge(projectTaskData?.status)}

              <div className="close-icon" onClick={onClose}>
                <MdClose />
              </div>
            </div>
          </div>
        </div>

        <div className="details-content">
          <div className="section">
            <h6 className="section-title">
              <MdInfo className="section-icon" /> Task Overview
            </h6>
            <div className="info-grid">
              <div className="info-item">
                <label>Project Name</label>
                <span className="value">{projectTaskData?.project?.name}</span>
              </div>

              <div className="info-item">
                <label>Start Date</label>
                <span className="value">
                  {formatDate(projectTaskData?.startDate)}
                </span>
              </div>
              <div className="info-item">
                <label>End Date</label>
                <span className="value">
                  {formatDate(projectTaskData?.endDate)}
                </span>
              </div>
            </div>
            {projectTaskData?.projectNotes && (
              <div className="notes-box">
                <strong>Notes:</strong>
                <p>{projectTaskData.projectNotes}</p>
              </div>
            )}
          </div>

          <div className="section">
            <h6 className="section-title">
              <MdAttachMoney className="section-icon" /> Status & Priority
            </h6>
            <div className="rates-grid">
              <div className="rate-item">
                <label>Status</label>
                <span className="value">
                  {getStatusBadge(projectTaskData?.status)}
                </span>
              </div>

              <div className="rate-item">
                <label>Task Status</label>
                <span className="value">
                  {getTaskStatusBadge(projectTaskData?.taskStatus)}
                </span>
              </div>
              <div className="rate-item">
                <label>Priority</label>
                <span className="value">
                  {getPriorityStatusBadge(projectTaskData?.priority)}
                </span>
              </div>
            </div>
          </div>

          {projectTaskData?.projectTaskMembers &&
            projectTaskData.projectTaskMembers.length > 0 && (
              <div className="section">
                <h6 className="section-title">
                  <MdPeople className="section-icon" /> Team Members (
                  {projectTaskData.projectTaskMembers.length})
                </h6>
                <div className="members-list">
                  {projectTaskData.projectTaskMembers.map((member) => {
                    const isExpanded = expandedMemberId === member.id;
                    return (
                      <div
                        key={member.id}
                        className={`member-card ${isExpanded ? 'expanded' : ''}`}
                        style={{ padding: '10px 12px' }}
                      >
                        <div
                          className="member-header"
                          style={{ borderBottom: 'none' }}
                        >
                          <div className="member-info">
                            <MdPerson className="member-icon" />
                            <p className="member-name">
                              {member.projectTaskEmployee?.firstName}{' '}
                              {member.projectTaskEmployee?.lastName}
                            </p>
                          </div>

                          <div
                            className="d-flex align-items-center"
                            style={{ gap: '0.5rem' }}
                          >
                            {getStatusBadge(member.status)}

                            <button
                              className="btn btn-sm btn-primary"
                              style={{ fontSize: '11px', padding: '2px 6px' }}
                              onClick={() => toggleMember(member.id)}
                            >
                              {isExpanded ? 'Hide' : 'View'}
                            </button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div
                            className="member-details"
                            style={{
                              marginTop: '8px',
                              paddingTop: '8px',
                              borderTop: '1px solid #e2e8f0',
                            }}
                          >
                            <div className="detail-row">
                              <span className="detail-label">Email:</span>
                              <span className="detail-value">
                                {member.projectTaskEmployee.email ||
                                  'Not provided'}
                              </span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Added:</span>
                              <span className="detail-value">
                                {formatDate(member.createdAt)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
        </div>
      </div>
    </Modal>
  );
};

export default ProjectTaskDetails;
